import { useEffect } from 'react';

/**
 * Custom hook for registering global keyboard shortcuts across the SaaS platform
 * 
 * Shortcuts:
 * - Cmd/Ctrl + K: Open global Command Palette
 * - Cmd/Ctrl + B: Toggle Navigation Rail sidebar
 * - Cmd/Ctrl + S: Trigger manual save (if inside form/editing context)
 * - Esc: Close active modals / slide-over drawers / command palette
 * - ? / Shift + /: Open keyboard shortcuts help cheat-sheet
 */
export const useKeyboardShortcuts = (shortcuts = {}) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? event.metaKey : event.ctrlKey;

      // Cmd/Ctrl + K -> Command Palette
      if (modifier && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        shortcuts.onOpenCommandPalette?.();
        return;
      }

      // Cmd/Ctrl + B -> Toggle Navigation Rail Sidebar
      if (modifier && event.key.toLowerCase() === 'b') {
        event.preventDefault();
        shortcuts.onToggleSidebar?.();
        return;
      }

      // Cmd/Ctrl + S -> Save
      if (modifier && event.key.toLowerCase() === 's') {
        event.preventDefault();
        shortcuts.onSave?.();
        return;
      }

      // Esc -> Escape / Close drawer / modal
      if (event.key === 'Escape') {
        shortcuts.onEscape?.();
        return;
      }

      // Shift + / (?) -> Shortcuts Cheat Sheet (only if not inside input/textarea)
      const isInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);
      if (!isInput && event.key === '?' && event.shiftKey) {
        event.preventDefault();
        shortcuts.onToggleHelp?.();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts]);
};

export default useKeyboardShortcuts;
